import React, { ReactNode } from "react";
import { I18nProvider } from "@lingui/react";
import { i18n } from "@lingui/core";
import { messages as catalogDe } from "../locale/de/messages";
import { messages as catalogEn } from "../locale/en/messages";
import { messages as catalogFr } from "../locale/fr/messages";
import { messages as catalogEs } from "../locale/es/messages";
import { connect } from "react-redux";
// @ts-ignore
import { setYupLocale } from "./yup";
// @ts-ignore
import { setLocale } from "../base/userActions";
import { withI18n, withI18nProps } from "../withI18n";
import { RootState } from "../base/store.types.ts";

export const defaultLocale = "de";
const catalogs = {
  de: catalogDe,
  en: catalogEn,
  fr: catalogFr,
  es: catalogEs,
};
export type Locale = keyof typeof catalogs;

// Activate a locale and keep <html lang> in sync. index.html ships lang="en"
// while the app defaults to German, so screen readers picked the wrong voice
// and browser-translate misfired (review #7).
function activateLocale(locale: string) {
  i18n.activate(locale);
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }
}

type LanguageInitializerProps = withI18nProps & { children?: ReactNode };

const LanguageInitializer = withI18n()(
  class extends React.Component<LanguageInitializerProps> {
    constructor(props: LanguageInitializerProps) {
      super(props);
      setYupLocale(props.i18n);
    }

    render = () => this.props.children;
  },
);

type ConnectedLanguageProviderInnerProps = {
  language: string;
  setLocale: (locale: string) => void;
  children?: ReactNode;
};

class ConnectedLanguageProviderInner extends React.Component<ConnectedLanguageProviderInnerProps> {
  constructor(props: ConnectedLanguageProviderInnerProps) {
    super(props);
    const params = new URLSearchParams(window.location.search);
    const locale = params.get("lang") || props.language;
    i18n.load(catalogs);
    if (locale) {
      props.setLocale(locale);
      activateLocale(locale);
    }
  }

  componentDidUpdate(
    prevProps: ConnectedLanguageProviderInnerProps,
    prevState: ConnectedLanguageProviderInnerProps,
  ) {
    if (prevProps.language !== this.props.language) {
      activateLocale(this.props.language);
    }
  }

  render = () => (
    <I18nProvider i18n={i18n}>
      <LanguageInitializer>{this.props.children}</LanguageInitializer>
    </I18nProvider>
  );
}

const clpProps = (state: RootState) => {
  return {
    language: state.user.locale || defaultLocale,
  };
};
const clpDispatch = (dispatch: any) => ({
  setLocale: (locale: string) => dispatch(setLocale(locale)),
});

export const ConnectedLanguageProvider = connect(
  clpProps,
  clpDispatch,
)(ConnectedLanguageProviderInner);

type URLBasedLanguageProviderProps = {
  children: ReactNode;
};

export class URLBasedLanguageProvider extends React.Component<URLBasedLanguageProviderProps> {
  constructor(props: URLBasedLanguageProviderProps) {
    super(props);
    const params = new URLSearchParams(window.location.search);
    const locale = params.get("lang") || defaultLocale;

    i18n.load(catalogs);
    activateLocale(locale);
  }

  render = () => (
    <I18nProvider i18n={i18n}>
      <LanguageInitializer>{this.props.children}</LanguageInitializer>
    </I18nProvider>
  );
}
